export interface EquipmentData {
  brand: string | null;
  color: number | null;
  del: boolean;
  desc: string | null;
  model: string | null;
  size: number | string | null;
  sport: number | null;
  type: number | null;
  unit: number | null;
}

export interface EquipmentOption {
  value: number;
  label: string;
}

export const EQUIP_DEFAULT_VALUES: EquipmentData = {
  brand: null,
  color: null,
  del: false,
  desc: null,
  model: null,
  size: null,
  sport: null,
  type: null,
  unit: null
};

// Equipment type options
export const EQUIPMENT_TYPES: EquipmentOption[] = [
  {value: 0, label: "Board"},
  {value: 1, label: "Sail"},
  {value: 2, label: "Kite"},
  {value: 3, label: "Boat"},
  {value: 4, label: "Kayak"},
  {value: 5, label: "Canoe"},
  {value: 6, label: "Surfski"},
  {value: 7, label: "Rowing"},
  {value: 8, label: "SUP"},
  {value: 9, label: "Fin"},
  {value: 10, label: "Foil"},
  {value: 11, label: "Wing"}
];

// Unit options
export const UNIT_OPTIONS: EquipmentOption[] = [
  {value: 0, label: "cm"},
  {value: 1, label: "m"},
  {value: 2, label: "ft"},
  {value: 3, label: "in"},
  {value: 4, label: "cm²"},
  {value: 5, label: "m²"},
  {value: 6, label: "ft²"},
  {value: 7, label: "lt"}
];

// Color options
export const COLOR_OPTIONS: EquipmentOption[] = [
  {value: 0, label: "Black"},
  {value: 1, label: "White"},
  {value: 2, label: "Red"},
  {value: 3, label: "Blue"},
  {value: 4, label: "Green"},
  {value: 5, label: "Yellow"},
  {value: 6, label: "Orange"},
  {value: 7, label: "Purple"},
  {value: 8, label: "Pink"},
  {value: 9, label: "Brown"},
  {value: 10, label: "Gray"},
  {value: 11, label: "Other"}
];

// Helper functions for equipment type and unit
export const getType = (type: number | null | undefined): string | null => {
  let typeStr: string | null = "";
  switch (type) {
    case 0:
      typeStr = "Board";
      break;
    case 1:
      typeStr = "Sail";
      break;
    case 2:
      typeStr = "Kite";
      break;
    case 3:
      typeStr = "Boat";
      break;
    case 4:
      typeStr = "Kayak";
      break;
    case 5:
      typeStr = "Canoe";
      break;
    case 6:
      typeStr = "Surfski";
      break;
    case 7:
      typeStr = "Rowing";
      break;
    case 8:
      typeStr = "SUP";
      break;
    case 9:
      typeStr = "Fin";
      break;
    case 10:
      typeStr = "Foil";
      break;
    case 11:
      typeStr = "Wing";
      break;
    default:
      typeStr = null;
  }
  return typeStr;
};

export const getUnit = (unit: number | null | undefined): string | null => {
  let unitStr: string | null = "";
  switch (unit) {
    case 0:
      unitStr = "cm";
      break;
    case 1:
      unitStr = "m";
      break;
    case 2:
      unitStr = "ft";
      break;
    case 3:
      unitStr = "in";
      break;
    case 4:
      unitStr = "cm²";
      break;
    case 5:
      unitStr = "m²";
      break;
    case 6:
      unitStr = "ft²";
      break;
    case 7:
      unitStr = "lt";
      break;
    default:
      unitStr = null;
  }
  return unitStr;
};

export class Equipment {
  brand: string | null;
  color: number | null;
  del: boolean;
  desc: string | null;
  model: string | null;
  size: number | string | null;
  sport: number | null;
  type: number | null;
  unit: number | null;

  constructor(equip: Partial<EquipmentData> | null | undefined) {
    if (!equip) {
      return;
    }
    this.brand = equip.brand ?? EQUIP_DEFAULT_VALUES.brand;
    this.color = equip.color ?? EQUIP_DEFAULT_VALUES.color;
    this.del = equip.del ?? EQUIP_DEFAULT_VALUES.del;
    this.desc = equip.desc ?? EQUIP_DEFAULT_VALUES.desc;
    this.model = equip.model ?? EQUIP_DEFAULT_VALUES.model;
    this.size = equip.size ?? EQUIP_DEFAULT_VALUES.size;
    this.sport = equip.sport ?? EQUIP_DEFAULT_VALUES.sport;
    this.type = equip.type ?? EQUIP_DEFAULT_VALUES.type;
    this.unit = equip.unit ?? EQUIP_DEFAULT_VALUES.unit;
  }

  getUnit(): string | null {
    return getUnit(this.unit);
  }

  getType(): string | null {
    return getType(this.type);
  }

  toString(withoutType: boolean = false): string {
    const type = this.getType();
    const sizeValue = Number(this.size);
    const showSize = Number.isFinite(sizeValue) && sizeValue > 0;
    const sizeLabel = showSize ? `${sizeValue} ${this.getUnit()}` : null;
    if (type && !withoutType) {
      return `${type}: ${this.brand} ${this.model}${sizeLabel ? ` (${sizeLabel})` : ""}`;
    }
    return `${this.brand} ${this.model}${sizeLabel ? ` (${sizeLabel})` : ""}`;
  }
}
